import { useQuery } from '@tanstack/react-query'
import type { SearchResponse, SearchEntityType } from '@/shared/schemas/search.schema'

const API_BASE = '/api/search'

// Query keys factory
export const SEARCH_KEYS = {
  all: ['search'] as const,
  query: (q: string, types?: SearchEntityType[]) =>
    [...SEARCH_KEYS.all, q, types?.join(',') ?? 'all'] as const,
}

interface UseGlobalSearchOptions {
  query: string
  types?: SearchEntityType[]
  limit?: number
  enabled?: boolean
}

/**
 * Hook for global search across all modules
 *
 * @param options.query - Search query string
 * @param options.types - Entity types to search (defaults to all)
 * @param options.limit - Max results per type (defaults to 5)
 * @param options.enabled - Whether to enable the query
 */
export function useGlobalSearch({
  query,
  types,
  limit = 5,
  enabled = true,
}: UseGlobalSearchOptions) {
  return useQuery({
    queryKey: SEARCH_KEYS.query(query, types),
    queryFn: async (): Promise<SearchResponse> => {
      const params = new URLSearchParams()
      params.set('q', query)
      if (types?.length) {
        params.set('types', types.join(','))
      }
      if (limit) {
        params.set('limit', String(limit))
      }

      const response = await fetch(`${API_BASE}?${params}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Search failed')
      }

      return response.json()
    },
    enabled: enabled && query.length >= 2, // Only search when query is at least 2 chars
    staleTime: 1000 * 30, // Cache results for 30 seconds
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}

/**
 * Group search results by entity type
 */
export function groupSearchResults(results: SearchResponse['results']) {
  const grouped: Record<SearchEntityType, SearchResponse['results']> = {
    contacts: [],
    companies: [],
    deals: [],
    cases: [],
    tasks: [],
    enquiries: [],
    products: [],
    notes: [],
    docs: [],
  }

  for (const result of results) {
    grouped[result.type].push(result)
  }

  return grouped
}
